import React, { useEffect, useState } from "react";
import "./OpsTicklesHighScores.css";
import { collection, getDocs, limit, orderBy, query } from "firebase/firestore";
import { db } from "../firebase/config";
import { useFirebase } from "../contexts/FirebaseContext";
import { useLanguage } from "../contexts/LanguageContext";

// Hard-coded — must never resolve to dinoHighScores / mazeHighScores.
const OPS_COLLECTION =
  process.env.REACT_APP_FIREBASE_ENV === "production" ||
  (process.env.REACT_APP_FIREBASE_ENV !== "development" &&
    process.env.NODE_ENV === "production")
    ? "production_opsTicklesPointsHighScores"
    : "opsTicklesPointsHighScores";

const isOpsRow = (row) => {
  if (row.gameId && row.gameId !== "opsTickles") return false;
  const points = Number(row.points ?? row.score ?? 0);
  // Ops-tickles scores are "2s shot" — Dino-run totals (700+) are rejected.
  if (!Number.isFinite(points) || points < 0 || points > 500) return false;
  return true;
};

const readOpsScores = async () => {
  if (!db) return [];

  try {
    const q = query(
      collection(db, OPS_COLLECTION),
      orderBy("points", "desc"),
      limit(10)
    );
    const snap = await getDocs(q);
    return snap.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .filter(isOpsRow);
  } catch (err) {
    // Empty / missing collection or missing index → blank board, never fall back to Dino.
    console.warn(
      `[Ops-tickles] Could not read ${OPS_COLLECTION}; showing blank board.`,
      err
    );
    return [];
  }
};

const OpsTicklesHighScores = ({ refreshKey = 0 }) => {
  const { userId } = useFirebase();
  const { t } = useLanguage();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      setLoading(true);
      if (refreshKey > 0) {
        await new Promise((r) => setTimeout(r, 400));
      }
      const list = await readOpsScores();
      if (!cancelled) {
        setRows(list);
        setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [refreshKey]);

  const formatDate = (timestamp) => {
    if (!timestamp) return "";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString();
  };

  return (
    <div className="ops-high-scores" data-leaderboard="ops-tickles-only">
      <div className="ops-high-scores-card">
        <h2 className="ops-high-scores-title">
          🚀 {t("opsTicklesHighScores")}
        </h2>

        {loading ? (
          <div className="ops-high-scores-loading">{t("loading")}</div>
        ) : rows.length === 0 ? (
          <div className="ops-high-scores-empty">{t("noHighScores")}</div>
        ) : (
          <div className="ops-high-scores-list">
            {rows.map((row, index) => (
              <div
                key={row.id}
                className={`ops-high-score-item ${
                  row.userId && userId && row.userId === userId ? "is-you" : ""
                }`}
              >
                <div className="ops-high-score-rank">#{index + 1}</div>
                <div className="ops-high-score-name">
                  {row.displayName || t("anonymous")}
                </div>
                <div className="ops-high-score-value">
                  {row.points ?? row.score ?? 0}
                </div>
                <div className="ops-high-score-date">
                  {formatDate(row.timestamp)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default OpsTicklesHighScores;
